import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { Bookmark, FileText, Image as ImageIcon, Download, Trash2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { db } from "../firebase";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import { auth } from "../firebase";

export default function Bookmarks() {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBookmarks();
  }, []);

  const fetchBookmarks = async () => {
    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) {
        setError("User not authenticated.");
        setLoading(false);
        return;
      }

      const lawyerId = user.uid;

      // Fetch bookmarks from lawyers/{userId}/bookmarks subcollection
      const bookmarksSnapshot = await getDocs(
        collection(db, "lawyers", lawyerId, "bookmarks")
      );

      const bookmarksData = bookmarksSnapshot.docs.map((document) => ({
        id: document.id,
        ...document.data(),
      }));

      setBookmarks(bookmarksData);
    } catch (err) {
      console.error("Firestore Fetch Error:", err);
      setError(`Failed to load bookmarks. Error: ${err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBookmark = async (bookmarkId) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      await deleteDoc(doc(db, "lawyers", user.uid, "bookmarks", bookmarkId));

      // Remove from local state
      setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
    } catch (err) {
      console.error("Error removing bookmark:", err);
      alert("Failed to remove bookmark");
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-[#0c0a29] text-white relative">
        <div className="background-circle circle-3 z-0"></div>
        <div className="background-circle circle-4 z-0"></div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Header */}
          <PageHeader
            title="Bookmarks"
            subtitle="Your saved acts, judgments and files"
            backTo="/portal/MyFiles"
          />

          {/* Content */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <div className="w-12 h-12 border-4 border-[#C99F4A] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <p className="text-red-400 text-lg">{error}</p>
            </div>
          )}

          {!loading && !error && bookmarks.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20">
              <Bookmark size={100} className="text-gray-600 mb-6" />
              <h2 className="text-2xl font-semibold text-gray-400 mb-2">No Bookmarks</h2>
              <p className="text-gray-500">You have not bookmarked any files yet</p>
            </div>
          )}

          {!loading && !error && bookmarks.length > 0 && (
            <div className="space-y-4">
              {bookmarks.map((bookmark) => (
                <BookmarkCard
                  key={bookmark.id}
                  bookmark={bookmark}
                  onRemove={() => handleRemoveBookmark(bookmark.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function BookmarkCard({ bookmark, onRemove }) {
  const fileName = bookmark.fileName || "Unknown File";
  const fileUrl = bookmark.fileUrl || bookmark.file_url || "";
  const caseNo = bookmark.caseNo || "";

  // Determine file type
  const isImage =
    fileName.toLowerCase().endsWith(".jpg") ||
    fileName.toLowerCase().endsWith(".jpeg") ||
    fileName.toLowerCase().endsWith(".png") ||
    fileName.toLowerCase().endsWith(".heic");
  const isPdf = fileName.toLowerCase().endsWith(".pdf");

  // Get file extension
  const fileExtension = fileName.split('.').pop()?.toUpperCase() || "FILE";

  // File color
  const getFileColor = () => {
    if (isPdf) return "#EF4444"; // Red
    if (isImage) return "#3B82F6"; // Blue
    return "#C99F4A"; // Gold
  };

  const FileIcon = () => {
    if (isPdf) return <FileText size={40} style={{ color: getFileColor(), opacity: 0.3 }} />;
    if (isImage) return <ImageIcon size={40} style={{ color: getFileColor(), opacity: 0.3 }} />;
    return <FileText size={40} style={{ color: getFileColor(), opacity: 0.3 }} />;
  };

  const handleOpenFile = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const handleDownload = () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
      <div className="flex items-center justify-between gap-6">
        {/* Left side - File info */}
        <div className="flex-1 min-w-0">
          {/* File Name */}
          <div className="mb-3">
            <p className="text-xs font-semibold text-gray-600 mb-1">File Name:</p>
            <h2 className="text-xl font-bold text-gray-900 truncate">{fileName}</h2>
          </div>

          {/* Case Number */}
          {caseNo && (
            <div className="mb-3">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Case No:</span> {caseNo}
              </p>
            </div>
          )}

          {/* File type badge and actions */}
          <div className="flex items-center gap-3">
            {/* File type badge */}
            <span
              className="px-3 py-1 rounded-lg text-xs font-bold"
              style={{
                backgroundColor: `${getFileColor()}26`,
                color: getFileColor(),
              }}
            >
              {fileExtension}
            </span>

            {/* Download button */}
            <button
              onClick={handleDownload}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              title="Download"
            >
              <Download size={16} className="text-gray-700" />
            </button>

            {/* Open button */}
            <button
              onClick={handleOpenFile}
              className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              title="Open"
            >
              <ExternalLink size={16} className="text-gray-700" />
            </button>

            {/* Remove bookmark button */}
            <button
              onClick={onRemove}
              className="p-2 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
              title="Remove Bookmark"
            >
              <Trash2 size={16} className="text-red-500" />
            </button>
          </div>
        </div>

        {/* Right side - File preview */}
        <div className="flex-shrink-0">
          <div
            onClick={handleOpenFile}
            className="w-24 h-28 rounded-xl bg-gray-100 border-2 border-gray-300 flex flex-col relative overflow-hidden cursor-pointer hover:border-[#C99F4A] transition-all"
          >
            {/* File icon/preview */}
            <div className="flex-1 flex items-center justify-center">
              <FileIcon />
            </div>

            {/* Bookmark indicator */}
            <div className="absolute top-2 right-2">
              <Bookmark size={16} className="text-[#C99F4A] fill-[#C99F4A]" />
            </div>

            {/* View button at bottom */}
            <div
              className="h-8 flex items-center justify-center gap-1 transition-colors"
              style={{
                backgroundColor: `${getFileColor()}E6`,
              }}
            >
              <ExternalLink size={14} className="text-white" />
              <span className="text-white text-xs font-semibold">Open</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
